import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { ChartSection } from '@/financeBotV2/src/components/dashboard/ChartSection';
import { FinanceV2Section } from '@/financeBotV2/src/components/dashboard/FinanceV2Section';
import { KPIGrid } from '@/financeBotV2/src/components/dashboard/KPIGrid';
import { LineageContext, LineagePanel } from '@/financeBotV2/src/components/dashboard/LineagePanel';
import { Button } from '@/financeBotV2/src/components/ui/button';
import { MarkdownContent } from '@/financeBotV2/src/components/ui/markdown-content';
import { transformDashboardResponse } from '@/financeBotV2/src/services/transformers';
import { ChartData, DashboardData, KPIMetric } from '@/financeBotV2/src/types/dashboard';
import ArFilterBar, { type ArFilterValues } from '@/pages/ArFilter';
import {
  getPreCannedSapReconnectErrorCode,
  isPreCannedSapReconnectError,
  PENDING_PRE_CANNED_SAP_REFRESH_KEY,
  preCannedReportsApi,
  PreCannedReportTemplateSummary,
  withInventoryFiscalPeriod,
} from '@/services/preCannedReportsService';
import { sapOAuthService } from '@/services/sapOAuthService';
import SmartMorningLeadersDashboard, { SmartMorningControlOption } from './SmartMorningLeadersDashboard';
import { buildResolvedSmartMorningPeriodOptions, buildSmartMorningPeriodOptions } from './smartMorningPeriodOptions';

const COMPARATOR_OPTIONS: SmartMorningControlOption[] = [
  { label: 'Prior Fiscal Year', value: 'Last Year' },
  { label: 'Plan / Budget', value: 'Plan / Budget' },
];

const buildDefaultParameters = (template?: PreCannedReportTemplateSummary | null): Record<string, string> => {
  if (!template) return {};
  const out: Record<string, string> = {};
  template.filters.forEach((filter) => {
    const value = filter.default ?? template.defaults?.[filter.id] ?? '';
    out[filter.id] = String(value ?? '');
  });
  return out;
};

const isPendingValidationDiagnostic = (item: Record<string, unknown>) => {
  const tool = String(item.tool ?? '');
  const source = String(item.source ?? '');
  const message = String(item.message ?? '');
  return tool.startsWith('missing.')
    || source.endsWith('_gap')
    || message.includes('Metric unavailable pending source/tool validation');
};


export default function PreCannedReportRenderPage() {
  const { templateId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PreCannedReportTemplateSummary[]>([]);
  const [rawResponse, setRawResponse] = useState<Record<string, unknown> | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [lineageContext, setLineageContext] = useState<LineageContext | null>(null);
  const [controlParameters, setControlParameters] = useState<Record<string, string>>({});
  const [periodOptions, setPeriodOptions] = useState<SmartMorningControlOption[]>(() => buildSmartMorningPeriodOptions());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) ?? null,
    [templateId, templates],
  );

  const beginSapReconnect = useCallback(async (
    parametersToRefresh: Record<string, string>,
    cause?: string | null,
  ) => {
    try {
      window.sessionStorage.setItem(
        PENDING_PRE_CANNED_SAP_REFRESH_KEY,
        JSON.stringify({
          templateId,
          parameters: parametersToRefresh,
          cause: cause ?? null,
        }),
      );
    } catch {
      // OAuth can still reconnect; the user may need to refresh again after returning.
    }
    setError('SAP Datasphere needs to be reconnected before SmartMorning can refresh.');
    try {
      await sapOAuthService.initiateConnection('smart-morning-for-leaders');
    } catch (err) {
      try {
        window.sessionStorage.removeItem(PENDING_PRE_CANNED_SAP_REFRESH_KEY);
      } catch {
        // No-op.
      }
      setError(err instanceof Error ? err.message : 'Unable to start SAP reconnect.');
      throw err;
    }
  }, [templateId]);

  const parameters = useMemo(() => {
    const out = buildDefaultParameters(selectedTemplate);
    searchParams.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }, [searchParams, selectedTemplate]);

  useEffect(() => {
    let mounted = true;
    void buildResolvedSmartMorningPeriodOptions(preCannedReportsApi.resolvePeriod)
      .then((options) => {
        if (mounted) setPeriodOptions(options);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const hydrate = useCallback(async (
    nextParameters = controlParameters,
    options: { forceRefresh?: boolean } = {},
  ) => {
    if (!templateId || !selectedTemplate) return;
    setLoading(true);
    setError(null);
    try {
      const enrichedParameters = withInventoryFiscalPeriod(nextParameters);
      const response = await preCannedReportsApi.hydrateTemplate(
        templateId,
        enrichedParameters,
        { forceRefresh: Boolean(options.forceRefresh) },
      );
      setRawResponse(response);
      setDashboardData(transformDashboardResponse(response as Parameters<typeof transformDashboardResponse>[0]));
    } catch (err) {
      if (isPreCannedSapReconnectError(err)) {
        await beginSapReconnect(
          withInventoryFiscalPeriod(nextParameters),
          getPreCannedSapReconnectErrorCode(err),
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Unable to render dashboard');
    } finally {
      setLoading(false);
    }
  }, [beginSapReconnect, controlParameters, selectedTemplate, templateId]);

  useEffect(() => {
    let mounted = true;
    preCannedReportsApi.listTemplates()
      .then((items) => {
        if (mounted) setTemplates(items);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : 'Unable to load templates');
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      const nextParameters = withInventoryFiscalPeriod(parameters);
      setControlParameters(nextParameters);
      void hydrate(nextParameters);
    }
  }, [selectedTemplate]);

  const handlePeriodChange = useCallback(async (value: string) => {
    const base = { ...controlParameters, tool_period: value };
    setControlParameters(base);
    try {
      const resolved = await preCannedReportsApi.resolvePeriod(value);
      const next = withInventoryFiscalPeriod({
        ...base,
        tool_period: resolved.tool_period || value,
        period_label: resolved.period_label,
        period: resolved.period,
        inventory_fiscal_period: resolved.inventory_fiscal_period,
      });
      setControlParameters(next);
      await hydrate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to resolve period');
    }
  }, [controlParameters, hydrate]);

  const handleComparisonChange = useCallback((value: string) => {
    const next = { ...controlParameters, comparison: value };
    setControlParameters(next);
    void hydrate(next);
  }, [controlParameters, hydrate]);

  const handleArFiltersApply = useCallback((filters: ArFilterValues) => {
  const next = {
    ...controlParameters,
    ...filters,
  };
  setControlParameters(next);
  void hydrate(next);
}, [controlParameters, hydrate]);

  const groupedCharts = dashboardData?.groups?.filter((group) => (group.charts?.length ?? 0) > 0) ?? [];
  const orphanCharts = dashboardData?.charts?.filter((chart) => (
    !groupedCharts.some((group) => group.charts.some((groupChart) => groupChart.graph_id === chart.graph_id || groupChart.title === chart.title))
  )) ?? [];
  const responseErrors = Array.isArray(rawResponse?.errors) ? rawResponse.errors as Array<Record<string, unknown>> : [];
  const visibleResponseErrors = responseErrors.filter((item) => !isPendingValidationDiagnostic(item));
  const templateTitle = selectedTemplate?.title || 'Pre-Canned Report';
  const smartMorningTemplateId = rawResponse?.meta && (rawResponse.meta as Record<string, unknown>).template_id;
  const isSmartMorningLeaders = rawResponse?.meta && (rawResponse.meta as Record<string, unknown>).template_id === 'smart_morning_finance';
  const isSmartMorningCommercial = rawResponse?.meta && (rawResponse.meta as Record<string, unknown>).template_id === 'smart_morning_commercial';
  const isOperationsDashboard = templateId === 'smart_morning_operations'
    || Boolean(rawResponse?.meta && (rawResponse.meta as Record<string, unknown>).template_id === 'smart_morning_operations');
  const isSmartMorningAr = templateId === 'smart_morning_ar'
    || Boolean(rawResponse?.meta && (rawResponse.meta as Record<string, unknown>).template_id === 'smart_morning_ar');
  const isSmartMorningDashboard = Boolean(isSmartMorningLeaders || isSmartMorningCommercial || isOperationsDashboard || isSmartMorningAr);

  const showKpiLineage = useCallback((metric: KPIMetric) => {
    setLineageContext({ kind: 'kpi', metric });
  }, []);

  const showChartLineage = useCallback((chart: ChartData) => {
    setLineageContext({ kind: 'chart', chart });
  }, []);

  const openOperationsDashboard = useCallback(() => {
    const search = new URLSearchParams();
    Object.entries(controlParameters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        search.set(key, String(value));
      }
    });
    navigate(`/smart-morning-for-leaders/render/smart_morning_operations?${search.toString()}`);
  }, [controlParameters, navigate]);

  const openArDashboard = useCallback(() => {
    const search = new URLSearchParams();
    Object.entries(controlParameters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        search.set(key, String(value));
      }
    });
    navigate(`/smart-morning-for-leaders/render/smart_morning_ar?${search.toString()}`);
  }, [controlParameters, navigate]);

  const openFinanceDashboard = useCallback(() => {
    navigate('/smart-morning-for-leaders');
  }, [navigate]);

  const handleForceRefresh = useCallback(() => {
    void hydrate(controlParameters, { forceRefresh: true });
  }, [controlParameters, hydrate]);

  const genericDashboardContent = (
    <>
      {dashboardData?.summaryText && (
        <FinanceV2Section title={dashboardData.title || templateTitle}>
          <MarkdownContent content={dashboardData.summaryText} className="text-sm leading-relaxed text-[#3F3338]" />
        </FinanceV2Section>
      )}

      {dashboardData?.metrics?.length ? (
        <KPIGrid metrics={dashboardData.metrics} onShowLineage={showKpiLineage} />
      ) : null}

      {groupedCharts.map((group) => (
        <ChartSection key={group.category} charts={group.charts} botMessageId="pre-canned-report-render" onShowChartLineage={showChartLineage} />
      ))}

      {orphanCharts.length > 0 && (
        <ChartSection charts={orphanCharts} botMessageId="pre-canned-report-render" onShowChartLineage={showChartLineage} />
      )}
    </>
  );

  return (
    <div className="min-h-full bg-[#FAF7F1] text-[#1F1A20]">
      <div className="flex w-full max-w-none flex-col gap-5 px-5 py-5 lg:px-7">
        {!isOperationsDashboard && (
        <div className="flex flex-col gap-3 border-b border-[#EDE6E1] pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold">{templateTitle}</h1>
            <p className="mt-1 text-sm text-[#7B6B72]">Rendered dashboard view. JSON authoring is hidden on this route.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/smart-morning-for-leaders')} className="h-9 rounded-md border-[#EDE6E1] bg-white px-3 text-sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Admin
            </Button>
            <Button type="button" onClick={() => void hydrate(controlParameters, { forceRefresh: true })} disabled={loading || !selectedTemplate} className="h-9 rounded-md bg-[#6E3450] px-3 text-sm text-white hover:bg-[#5A2A41]">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Force Refresh
            </Button>
          </div>
        </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {visibleResponseErrors.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {visibleResponseErrors.map((item, index) => (
              <div key={`${String(item.source ?? 'error')}-${index}`}>
                {String(item.source ?? 'Source')}: {String(item.message ?? 'Hydration warning')}
              </div>
            ))}
          </div>
        )}
        {isSmartMorningAr && (
  <ArFilterBar
    filters={selectedTemplate?.filters ?? []}
    parameters={controlParameters}
    disabled={loading || !selectedTemplate}
    onApply={handleArFiltersApply}
  />
)}
        {loading && (
          <FinanceV2Section title="Loading">
            <p className="text-sm text-[#7B6B72]">Checking for updates...</p>
          </FinanceV2Section>
        )}

        {!loading && isSmartMorningDashboard && rawResponse ? (
          <SmartMorningLeadersDashboard
            key={String(smartMorningTemplateId)}
            response={rawResponse}
            periodOptions={periodOptions}
            comparisonOptions={COMPARATOR_OPTIONS}
            onPeriodChange={handlePeriodChange}
            onComparisonChange={handleComparisonChange}
            controlsDisabled={loading}
            loading={loading}
            onForceRefresh={handleForceRefresh}
            forceRefreshDisabled={loading || !selectedTemplate}
            activeTab={isSmartMorningAr ? 'ar' : isOperationsDashboard ? 'operations' : smartMorningTemplateId === 'smart_morning_commercial' ? 'commercial' : 'finance'}
            onCommercialClick={() => navigate('/smart-morning-for-leaders/render/smart_morning_commercial')}
            onFinanceClick={openFinanceDashboard}
            onOperationsClick={openOperationsDashboard}
            onArClick={openArDashboard}
            contentOverride={isOperationsDashboard ? genericDashboardContent : undefined}
          />
        ) : null}

        {!loading && !isSmartMorningDashboard && (
          <>
            {genericDashboardContent}
          </>
        )}
      </div>
      <LineagePanel context={lineageContext} onClose={() => setLineageContext(null)} />
    </div>
  );
}
